import { bind } from 'bind-decorator';
import type { Uuid } from '@common/utils/uuid';
import type { IInsurerServiceDeclarationRule } from '@frontend/components/interfaces/insurerServiceDeclarationRule';
import type { FundingServicePart } from '@frontend/services/api/fundingServicePartsApiService';
import { getFundingServiceParts } from '@frontend/services/api/fundingServicePartsApiService';
import type { IInsurerServiceDeclarationSetting } from '../../interfaces/insurerServiceDeclarationSetting';
import { fetchJson } from '../../../store/fetch';
import { getSelectedLanguage } from '../../../translate';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    Header,
    LinkButton,
    Loading,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

interface IInsurerServiceDeclarationSettingListProps {
    insurerId?: string;
    insurerServiceDeclarationRuleId: string;
    returnPage?: string;
    serviceId?: string;
}

interface IInsurerServiceDeclarationSettingListState {
    authorization: {
        canAdd: boolean;
    };
    errorMessage?: string;
    fundingServiceParts: FundingServicePart[];
    insurerServiceDeclarationRule?: IInsurerServiceDeclarationRule;
    insurerServiceDeclarationSettings: IInsurerServiceDeclarationSetting[];
    isSearching: boolean;
    page: number;
    total: number;
}

export class InsurerServiceDeclarationSettingList extends Component<
    IInsurerServiceDeclarationSettingListProps,
    IInsurerServiceDeclarationSettingListState
> {
    private readonly abortController = new AbortController();

    constructor(props: IInsurerServiceDeclarationSettingListProps, context?: any) {
        super(props, context);
        this.state = {
            authorization: {
                canAdd: false,
            },
            fundingServiceParts: [],
            insurerServiceDeclarationSettings: [],
            isSearching: true,
            page: 1,
            total: 0,
        };
    }

    public componentDidMount() {
        this.search();
        this.loadRule();
    }

    public componentWillUnmount() {
        this.abortController.abort();
    }

    public render() {
        const translate = getSelectedLanguage();

        let actionButtons: JSX.Element[] | undefined;
        if (this.state.authorization.canAdd) {
            actionButtons = [
                <LinkButton
                    icon="add"
                    key="add"
                    to={this.getUrlAddEdit()}
                >
                    {translate.serviceDeclarationRules.addDeclarationSetting}
                </LinkButton>,
            ];
        }

        return (
            <>
                <Header
                    actionButtons={actionButtons}
                    content={translate.serviceDeclarationRules.declarationSettings}
                    smallHeader
                />
                <Loading
                    item={this.state.insurerServiceDeclarationSettings}
                    loaded={(insurerServiceDeclarationSettings) => (
                        <>
                            <AdminTable>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{translate.common.startDate}</TableHeader>
                                        <TableHeader>{translate.common.endDate}</TableHeader>
                                        <TableHeader>{translate.finance.declaration}</TableHeader>
                                        <TableHeader>{translate.insurers.careProductType}</TableHeader>
                                        <TableHeader>{translate.finance.fundingServicePart.shortName}</TableHeader>
                                        <TableHeader />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {insurerServiceDeclarationSettings.map((insurerServiceDeclarationSetting) => (
                                        <TableRow key={insurerServiceDeclarationSetting.id}>
                                            <TableData>
                                                <NullableFormattedDate
                                                    value={insurerServiceDeclarationSetting.startDate}
                                                />
                                            </TableData>
                                            <TableData>
                                                <NullableFormattedDate
                                                    value={insurerServiceDeclarationSetting.endDate}
                                                />
                                            </TableData>
                                            <TableData>
                                                {
                                                    translate.declarationMethodEnum[
                                                        insurerServiceDeclarationSetting.declarationMethod
                                                    ]
                                                }
                                            </TableData>
                                            <TableData>{insurerServiceDeclarationSetting.careProductType}</TableData>
                                            <TableData>
                                                {insurerServiceDeclarationSetting.fundingServicePartId
                                                    ? this.state.fundingServiceParts.find(
                                                          ({ id }) =>
                                                              id ===
                                                              insurerServiceDeclarationSetting.fundingServicePartId
                                                      )?.name
                                                    : translate.common.all}
                                            </TableData>
                                            {insurerServiceDeclarationSetting.authorization.canUpdate && (
                                                <TableData align="right">
                                                    <EditButton
                                                        to={this.getUrlAddEdit(insurerServiceDeclarationSetting.id)}
                                                    />
                                                    {insurerServiceDeclarationSetting.authorization.canDelete && (
                                                        <DeleteButton
                                                            onClick={() =>
                                                                this.remove(insurerServiceDeclarationSetting.id)
                                                            }
                                                        />
                                                    )}
                                                </TableData>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </AdminTable>
                            <ListPager
                                hasMoreItems={true}
                                isDisabled={this.state.isSearching}
                                onChangePage={this.handleNavigation}
                                page={this.state.page}
                                total={this.state.total}
                            />
                        </>
                    )}
                />
            </>
        );
    }

    private getUrlAddEdit(insurerServiceDeclarationSettingId?: string) {
        let url = '';

        if (insurerServiceDeclarationSettingId) {
            url = `/insurerServiceDeclarationSettings/${insurerServiceDeclarationSettingId}/edit`;
        } else {
            url = '/insurerServiceDeclarationSettings/add';
        }

        url += `?insurerServiceDeclarationRuleId=${this.props.insurerServiceDeclarationRuleId}`;

        if (this.props.insurerId) {
            url += `&insurerId=${this.props.insurerId}`;
        }
        if (this.props.serviceId) {
            url += `&serviceId=${this.props.serviceId}`;
        }
        if (this.props.returnPage) {
            url += `&returnPage=${this.props.returnPage}`;
        }

        return url;
    }

    private async search() {
        const url = `/api/insurerServiceDeclarationSettings?insurerServiceDeclarationRuleId=${this.props.insurerServiceDeclarationRuleId}&includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${this.state.page}`;
        await this.setState({ isSearching: true });
        const result = await fetchJson(url, 'GET');
        await this.setState({
            authorization: result.json.authorization,
            insurerServiceDeclarationSettings: result.json.items,
            isSearching: false,
            total: result.json.total,
        });
    }

    @bind
    private async handleNavigation(page: number) {
        await this.setState({ page: page });
        await this.search();
    }

    private async remove(id: string) {
        const url = `/api/insurerServiceDeclarationSettings/${id}`;
        await this.setState({ isSearching: true });
        await fetchJson(url, 'DELETE');
        await this.search();
    }

    private async loadRule() {
        const url = `/api/insurerServiceDeclarationRules/${this.props.insurerServiceDeclarationRuleId}`;
        const result = await fetchJson({ method: 'GET', url });
        await this.setState({ insurerServiceDeclarationRule: result.json });
        await this.loadFundingServiceParts();
    }

    private async loadFundingServiceParts() {
        const serviceId = this.state.insurerServiceDeclarationRule?.serviceId;
        if (!serviceId) {
            throw new Error('Service id is required');
        }
        const fundingServiceParts = await getFundingServiceParts(serviceId as Uuid, this.abortController.signal);
        fundingServiceParts.sort((a, b) => a.name.localeCompare(b.name));
        await this.setState({ fundingServiceParts });
    }
}
